import type { PiniaPluginContext } from 'pinia'
import type { StateHandler } from 'v3-infinite-loading/lib/types'
import type { VForm } from 'vuetify/components/VForm'
import type { ISearch } from '@/components/CDF/SearchBar.vue'
import type { IOrderBy } from '@/pages/types/layoutTable.types'
import { useConfirmDialogStore } from '@/stores/useConfirmDialogStore'
import { useSnackbarStore } from '@/stores/useSnackbarStore'

const loadedServices = {}

const modules = import.meta.glob('@/pages/admin/**/services/*.ts')

const preloadServices = modules => {
  // Utilize import.meta.glob para buscar arquivos correspondentes
  const keys = Object.keys(modules)

  if (keys.length === 0)
    throw new Error('Nenhum serviço encontrado.')

  // Carregar todos os módulos de serviço
  const loadPromises = keys.map(key =>
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    modules[key]().then(ServiceModule => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      const serviceName = key.split('/').pop().replace('.ts', '')

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      loadedServices[serviceName] = ServiceModule.default
    }),
  )

  return Promise.all(loadPromises)
}

const getService = (serviceName: string) => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  const service = loadedServices[serviceName]

  if (!service)
    throw new Error(`Serviço ${serviceName} não encontrado.`)

  return service
}

// Durante a inicialização da aplicação, pré-carregar todos os serviços
preloadServices(modules).then(() => {
  console.log('Todos os serviços foram pré-carregados.')
}).catch(error => {
  console.error('Erro ao pré-carregar os serviços:', error)
})

export async function CodifyTechStorePlugin({ store }: PiniaPluginContext) {
  if (store.$id.includes('crud')) {
    const confirmDialog = useConfirmDialogStore()
    const snackbar = useSnackbarStore()

    const service = getService(store.serviceName)

    const form = ref<VForm | undefined>(undefined)
    const formKey = ref<number>(Math.random())

    const data = ref<typeof store.$state.defaultValue>({
      ...store.$state.defaultValue,
    })

    const items = ref<any[]>([])
    const itemsPerPage = ref<number>(0)
    const page = ref<number>(1)
    const totalItems = ref<number>(0)

    const orderBy = ref<IOrderBy>({
      key: store.$state.sortKeyDefault,
      order: 'asc',
    })

    const isSearching = ref<boolean>(false)

    const searchTerm = ref<ISearch>({
      field: '',
      search: '',
      relationship: '',
    })

    const searchPage = ref<number>(1)
    const destroyId = ref<string>('')

    const loading = ref({
      save: false,
      item: false,
      items: false,
      destroy: false,
    })

    const savingFields = reactive<Record<string, boolean>>({})

    // Definir a variável na `$state`, permite que esta seja
    // serializada durante a interpretação do lado do servidor
    store.$state.form = form.value
    store.$state.formKey = formKey.value
    store.$state.data = data.value
    store.$state.items = items.value
    store.$state.itemsPerPage = itemsPerPage.value
    store.$state.page = page.value
    store.$state.totalItems = totalItems.value
    store.$state.orderBy = orderBy.value
    store.$state.isSearching = isSearching.value
    store.$state.searchTerm = searchTerm.value
    store.$state.searchPage = searchPage.value
    store.$state.destroyId = destroyId.value
    store.$state.loading = loading.value

    const reactiveKeys = [
      'form',
      'formKey',
      'data',
      'items',
      'itemsPerPage',
      'page',
      'totalItems',
      'orderBy',
      'isSearching',
      'searchTerm',
      'searchPage',
      'destroyId',
      'loading',
    ]

    reactiveKeys.forEach(key => {
      store[key] = toRef(store.$state, key)
    })

    const originalReset = store.$reset.bind(store)

    const actions = {
      async loadMore(state: StateHandler) {
        if (store.isSearching)
          await store.search(state)
        else await store.fetchItems(state)
      },
      async fetchItems(state: StateHandler) {
        if (store.isSearching) {
          store.items = []
          store.totalItems = 0
          store.page = 1
        }

        if (store.page === 1)
          store.loading.items = true

        store.isSearching = false

        try {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          const { data } = await service.fetchAll<IItemsResponse<T>>({
            page: store.page,
            sort_by: store.orderBy.key,
            sort_order: store.orderBy.order,
          })

          if (data.data.length === 0) {
            state.complete()
          }
          else {
            if (data.page === store.page) {
              store.items.push(...data.data)
              state.loaded()
            }
          }
          store.totalItems = data.total
          store.page = data.page + 1
        }
        catch (error) {
          store.items = []
        }
        finally {
          store.loading.items = false
        }
      },
      async fetchItem(id: string) {
        store.loading.item = true

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        await service.fetch<T>(id)
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          .then(({ data: item }) => {
            store.data = item

            return item
          })
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          .catch(err => {
            return err
          })
          .finally(() => {
            store.loading.item = false
          })
      },
      async save(formData?: boolean) {
        store.loading.save = true

        return await service.create(store.data, null, formData)
          .then(({ data }) => {
            store.resetForm()
            store.formKey = Math.random()

            snackbar.showSnackbar({
              text: 'Foi criado com sucesso!',
              color: 'success',
              location: 'top right',
              multiline: true,
              timeout: 4000,
            })

            return data
          })
          .catch(err => {
            return err.response.data
          })
          .finally(() => {
            store.loading.save = false
          })
      },
      async update(formData?: boolean) {
        if (store.data.id) {
          store.loading.save = true

          return await service.update(store.data, store.data.id, formData)
            .then(({ data }) => {
              if (data)
                store.data = data

              snackbar.showSnackbar({
                text: 'Foi atualizado com sucesso!',
                color: 'success',
                location: 'top right',
                multiline: true,
                timeout: 4000,
              })

              return data
            })
            .catch(err => {
              return err.response.data
            })
            .finally(() => {
              store.loading.save = false
            })
        }
      },
      async updateField(field: string, value: any) {
        if (!store.data?.id)
          return

        savingFields[field] = true

        console.log(field, value)

        try {
          const response = await service.patch({ [field]: value }, store.data.id)

          if (response?.data)
            store.data[field] = response.data[field]

          snackbar.showSnackbar({
            text: 'Foi atualizado com sucesso!',
            color: 'success',
            location: 'top right',
            multiline: true,
            timeout: 4000,
          })
        }
        catch (e) {
          console.error(`Erro ao salvar campo ${field}`, e)
        }
        finally {
          savingFields[field] = false
        }
      },
      async destroy() {
        store.loading.destroy = true
        try {
          await service.destroy(store.destroyId)

          const index = store.items.findIndex(item => item.id === store.destroyId)

          store.items.splice(index, 1)

          snackbar.showSnackbar({
            text: 'Foi excluído com sucesso!',
            color: 'success',
            location: 'top right',
            multiline: true,
            timeout: 4000,
          })
        }
        finally {
          store.loading.destroy = false
        }
      },
      async dialogDestroy(id: string) {
        confirmDialog.showConfirmDialog('Deseja realmente excluir este item?', 'Sim', 'Não', store.loading.destroy, async () => {
          await store.destroy()
        })
        store.destroyId = id
      },
      async search(state: StateHandler) {
        if (!store.isSearching) {
          store.items = []
          store.totalItems = 0
          store.searchPage = 1
        }

        if (store.searchPage === 1)
          store.loading.items = true

        store.isSearching = true

        if (store.$state.searchTerm.field && String(store.$state.searchTerm.search).length > 0) {
          try {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            const { data } = await service.search<IItemsResponse<T>>(
              store.$state.searchTerm.field,
              store.$state.searchTerm.search,
              store.searchPage,
              store.$state.searchTerm.relationship,
              store.orderBy,
            )

            if (data.data.length === 0) {
              state.complete()
            }
            else {
              if (data.page === store.searchPage) {
                store.items.push(...data.data)
                state.loaded()
              }
            }
            store.totalItems = data.total
            store.searchPage = data.page + 1
          }
          catch {
            store.items = []
          }
          finally {
            store.loading.items = false
          }
        }
      },
      async onOrderBy(state: StateHandler, value: IOrderBy[]) {
        store.orderBy = value.length > 0
          ? { key: value[0].key, order: value[0].order }
          : { key: store.sortKeyDefault, order: 'asc' }

        store.items = []
        store.searchPage = 1
        store.page = 1
        store.loading.items = false

        await store.loadMore(state)
      },
      debouncedUpdateField(field: string, value: string) {
        console.log(`Debounced update for field: ${field}, value: ${value}`)

        const updateFn = useDebounceFn((val: any) => {
          store.updateField(field, val)
        }, 800)

        return updateFn(value)
      },

      $reset() {
        originalReset()
      },
      reset() {
        store.items = []
        store.searchPage = 1
        store.page = 1
      },
      resetForm() {
        store?.form?.reset()
        store?.form?.resetValidation()

        store.data = { ...store.defaultValue }
        for (const key in store.defaultValue) {
          if (Array.isArray(store.defaultValue[key]))
            store.data[key] = []
        }
        store.reset()
      },
      resetSearch(state: StateHandler) {
        store.searchTerm = {
          field: '',
          search: '',
          relationship: '',
        }
        store.searchPage = 1
        store.isSearching = false
        store.page = 1
        store.items = []
        store.fetchItems(state)
      },
      searchAgain(state: StateHandler) {
        store.items = []
        store.searchPage = 1
        store.search(state)
      },
    }

    store = Object.assign(store, actions)

    return store
  }

  return store
}
